import React, { useEffect, useMemo } from 'react'
import { capitalize } from 'lodash-es'
import { Trophy } from 'lucide-react'

import { useTenant } from '@rewardops/auth'
import { Skeleton, usePAWStore } from '@rewardops/ui'
import { getComplementColor, lightenColor } from '@rewardops/utils'

import { replacePlaceholders } from '~/utils/translation-utils'
import { useApi } from '~/components/hooks/use-api'
import { useFormat } from '~/components/hooks/use-format'
import { useTranslation } from '~/app/i18n/client'
import { PAWSectionType } from './paw-section-types'

interface ProgressBarProps {
  name?: string
  current: number
  target: number
  currentTier: string
  nextTier: string
  accumulators: {
    name: string
    value: number
  }[]
}

const ProgressCard = ({
  name,
  current,
  target,
  currentTier,
  nextTier,
  accumulators,
}: ProgressBarProps) => {
  const percentage =
    current === 0 && target === 0
      ? 0
      : Number(Math.min((current / target) * 100, 100).toFixed(2))

  const tenant = useTenant()

  const remaining = Math.max(target - current, 0)

  const { formatNumber } = useFormat()
  const { t: translate } = useTranslation()

  const gradientColor = getComplementColor(
    tenant?.customStyling?.primaryColor ?? '#FFFFFF',
  )

  const hoverColor = lightenColor(
    tenant?.customStyling?.primaryColor ?? '#FFFFFF',
    62,
  )

  const currency = accumulators?.[0]?.name ?? ''

  return (
    <div className="rounded-md border bg-white shadow-sm">
      <div className="flex flex-col space-y-1.5 p-6 pb-3">
        <div className="flex items-center justify-between gap-4">
          <div className="text-lg font-semibold tracking-tight">{name}</div>
          <div
            className={`flex items-center gap-1 rounded-full border border-tenant-primary-color 
        bg-[var(--hover-color)] px-3 py-1`}
            style={
              {
                '--hover-color': hoverColor,
              } as React.CSSProperties
            }
          >
            <Trophy size={14} className="text-tenant-primary-color" />
            <div className="text-sm text-tenant-primary-color">
              {currentTier}
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-6 p-6 pt-0">
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              {replacePlaceholders(
                translate('pawDashboards.tierProgress.progressTo'),
                {
                  tierName: nextTier,
                },
              )}
            </div>
            <div className="font-medium">{percentage}%</div>
          </div>
          <div className="relative h-3 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-gradient-to-r from-tenant-primary-color to-[var(--gradient-to)]"
              style={
                {
                  width: `${percentage}%`,
                  '--gradient-to': gradientColor,
                } as React.CSSProperties
              }
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <div className="text-sm text-muted-foreground">
              {formatNumber(current)} {capitalize(currency)}
            </div>
            <div className="text-sm text-muted-foreground">
              {formatNumber(remaining)} {capitalize(currency)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const TierProgress: React.FC<PAWSectionType> = ({
  content,
  data,
}: PAWSectionType) => {
  const { api } = useApi()
  const { t: translate } = useTranslation()
  const { setTier } = usePAWStore((state) => state)

  const { data: memberChallenges, isLoading } =
    api.pawDashboards.getMemberChallenges.useQuery({
      fetchHistorical: false,
      ...(content?.metadata?.params ?? {}),
    })

  const getCurrentTotal = (accumulatorMap: Record<string, number>): number => {
    return Object.values(accumulatorMap).reduce((acc, value) => acc + value, 0)
  }

  const currentTier = useMemo(() => {
    if (memberChallenges?.challenges?.length) {
      const tiers = memberChallenges?.challenges?.map(
        (challenge: any) => challenge?.currentTier?.name,
      )

      const filteredTiers = Array.from(new Set(tiers))

      return `${filteredTiers?.join(', ')}`
    }

    return ''
  }, [memberChallenges?.challenges])

  useEffect(() => {
    if (currentTier) {
      setTier(currentTier)
    }
  }, [currentTier, setTier])

  return (
    <div
      id={content?.sectionId}
      data-testid={content?.sectionId}
      className="lg:col-span-8"
    >
      <h3 className="mb-6 text-xl font-semibold">{content?.title}</h3>
      {isLoading ? (
        <Skeleton className="h-[300px] w-full" />
      ) : memberChallenges?.challenges?.length ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {memberChallenges?.challenges?.map((challenge: any) => (
            <React.Fragment key={challenge?.muInstanceId}>
              <ProgressCard
                name={challenge?.name ?? ''}
                current={getCurrentTotal(challenge?.accumulatorMap)}
                target={challenge?.targetTier?.thresholds?.[0]?.target ?? 0}
                currentTier={challenge?.currentTier?.name ?? ''}
                nextTier={challenge?.targetTier?.name ?? ''}
                accumulators={challenge?.accumulators ?? []}
              />
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="text-sm text-slate-800">
          {translate('pawDashboards.noDataFound')}
        </div>
      )}
    </div>
  )
}
